<template>
  <div class="mails-wrapper">
    <div v-if="!userMails" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary"></b-spinner>
      <p class="text-muted mt-2">Loading mails...</p>
    </div>

    <div v-else class="animated fadeIn">
      <b-row v-if="userMails.length === 0">
        <b-col>
          <b-alert show variant="info" class="text-center">
            No mails found for this user.
          </b-alert>
        </b-col>
      </b-row>

      <div v-else>
        <b-row>
          <b-col v-for="mail in userMails" :key="mail.id" cols="12" lg="6">
            <b-card class="mb-3 shadow-sm bg-dark text-white" :class="mail.is_deleted ? 'border-secondary' : 'border-left-info'">
              <template #header>
                <div class="d-flex justify-content-between align-items-center" :class="{'opacity-50': mail.is_deleted}">
                  <div>
                    <i class="fa fa-envelope" :class="mail.is_deleted ? 'text-secondary' : 'text-primary'"></i>
                    <strong class="ml-2 text-white">{{ mail.subject || '(No Subject)' }}</strong>
                    <span class="text-muted small ml-2">from {{ mail.sender }}</span>
                    <b-badge v-if="mail.is_deleted" variant="secondary" class="ml-2">Deleted</b-badge>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="text-muted small mr-3">ID: #{{ mail.id }}</span>
                    <b-button v-if="hasPermission(UserMailsRestore) && mail.is_deleted" size="sm" variant="outline-success" class="mr-2" @click="confirmRestore(mail)">
                      <i class="fa fa-undo"></i>
                    </b-button>
                    <b-button v-if="hasPermission(UserMailsDelete) && !mail.is_deleted" size="sm" variant="outline-danger" @click="confirmDelete(mail)">
                      <i class="fa fa-trash"></i>
                    </b-button>
                  </div>
                </div>
              </template>

              <div class="mail-content mb-3 text-light" :class="{'opacity-50': mail.is_deleted}" style="white-space: pre-wrap; max-height: 200px; overflow-y: auto;">
                <template v-if="mail.content === '[REDACTED - NO PERMISSION]'">
                  <div class="text-center p-3 bg-darker rounded border border-warning text-warning">
                    <i class="fa fa-lock mr-2"></i> Content Redacted - You do not have permission to read mail content.
                  </div>
                </template>
                <template v-else>
                  {{ mail.content }}
                </template>
              </div>

              <div v-if="mail.items && mail.items.length > 0" class="mail-items p-2 bg-dark-subtle rounded border border-secondary">
                <h6 class="mb-2 small font-weight-bold text-uppercase text-muted">Attached Items</h6>
                <div class="d-flex flex-wrap">
                  <div v-for="item in mail.items" :key="item.id" 
                       class="item-badge mr-2 mb-2 p-1 px-2 border-0 rounded d-flex align-items-center shadow-sm"
                       :class="item.is_deleted ? 'bg-dark text-muted opacity-50' : 'bg-secondary text-white'">
                    <span class="mr-2" :class="{'text-decoration-line-through': item.is_deleted}">{{ item.item_name }}</span>
                    <b-badge variant="info" class="mr-2" :class="{'opacity-50': item.is_deleted}">x{{ item.quantity }}</b-badge>
                    
                    <b-button v-if="hasPermission(UserMailsRestoreItem) && item.is_deleted" size="sm" variant="link" class="p-0 text-success" @click="confirmRestoreItem(mail, item)">
                      <i class="fa fa-undo"></i>
                    </b-button>
                    
                    <b-button v-if="hasPermission(UserMailsDelete) && !item.is_deleted" size="sm" variant="link" class="p-0 text-white" @click="confirmDeleteItem(mail, item)">
                      <i class="fa fa-trash-o"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'UserMails',
  props: {
    username: { type: String, required: true },
    server: { type: String, required: true }
  },
  data() {
    return {
    }
  },
  computed: {
    ...mapGetters(['userMails'])
  },
  watch: {
    username: 'fetchData',
    server: 'fetchData'
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      if (this.username && this.server) {
        this.emit('getUserMails', { username: this.username, server: this.server })
      }
    },
    confirmDelete(mail) {
      this.$bvModal.msgBoxConfirm(`Are you sure you want to mark this mail from ${mail.sender} as deleted?`, {
        title: 'Delete Mail',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.emit('deleteUserMail', { 
            mail_id: mail.id, 
            server: this.server,
            username: this.username 
          })
          // Optimistic update
          mail.is_deleted = true
        }
      })
    },
    confirmDeleteItem(mail, item) {
      this.$bvModal.msgBoxConfirm(`Are you sure you want to mark the item ${item.item_name} as deleted?`, {
        title: 'Delete Mail Item',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.emit('deleteMailItem', {
            mail_id: mail.id,
            item_id: item.id,
            server: this.server
          })
          // Optimistic update
          item.is_deleted = true
        }
      })
    },
    confirmRestore(mail) {
      this.$bvModal.msgBoxConfirm(`Are you sure you want to restore this mail from ${mail.sender}?`, {
        title: 'Restore Mail',
        okVariant: 'success',
        okTitle: 'Restore',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.emit('restoreUserMail', { 
            mail_id: mail.id, 
            server: this.server,
            username: this.username 
          })
          // Optimistic update
          mail.is_deleted = false
        }
      })
    },
    confirmRestoreItem(mail, item) {
      this.$bvModal.msgBoxConfirm(`Are you sure you want to restore the item ${item.item_name}?`, {
        title: 'Restore Mail Item',
        okVariant: 'success',
        okTitle: 'Restore',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.emit('restoreMailItem', {
            mail_id: mail.id,
            item_id: item.id,
            server: this.server
          })
          // Optimistic update
          item.is_deleted = false
        }
      })
    }
  }
}
</script>

<style scoped>
.bg-dark {
  background-color: #1e1e1e !important;
}
.bg-dark-subtle {
  background-color: #2a2a2a !important;
}
.bg-darker {
  background-color: #121212 !important;
}
.border-left-info {
  border-left: 4px solid #63c2de !important;
}
.item-badge {
  font-size: 0.85rem;
}
.text-decoration-line-through {
  text-decoration: line-through;
}
.opacity-50 {
  opacity: 0.5;
}

/* Animations */
.card {
  transition: border-color 0.3s ease-in-out;
}
.card-header > div,
.mail-content {
  transition: opacity 0.3s ease-in-out;
}
.item-badge {
  transition: all 0.3s ease-in-out;
}
.fa-envelope {
  transition: color 0.3s ease-in-out;
}
</style>
